import { type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// Every Home section is a stack of tappable rows sharing one shape, so the
// row chrome lives here instead of being re-styled per section.

export function HomeRowList({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2">{children}</div>;
}

export function HomeRow({
  onClick,
  children,
  className,
}: {
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  // A div, not a <button>: some rows nest an <a> (live URL) and nested
  // interactive elements are invalid HTML. Same pattern as SessionList rows.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "glass glass-border flex w-full cursor-pointer items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-secondary/40",
        className,
      )}
    >
      {children}
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60" />
    </div>
  );
}

export function HomeRowsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="glass glass-border flex items-center gap-3 rounded-xl px-4 py-3">
          <Skeleton className="h-4 w-4 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-2.5 w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ViewAllAction({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
    >
      View all
    </button>
  );
}
